import { supabase } from '../lib/supabase';
import { PDFGenerator } from './pdfGenerator';
import { User, Post } from '../types';

interface SimpleUserRef {
  id: string;
  username?: string;
  full_name?: string;
  avatar_url?: string;
}

interface SimplePostRef {
  id: string;
  title?: string;
  media_files?: Array<{ url: string; type: string }>;
}

export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account' | 'paypal';
  last_four?: string;
  brand?: string;
  exp_month?: number;
  exp_year?: number;
  billing_name?: string;
  stripe_payment_method_id?: string;
  is_default: boolean;
  is_active: boolean;
}

export interface Transaction {
  id: string;
  type: 'sale' | 'refund' | 'payout' | 'fee';
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'cancelled' | 'refunded';
  amount: number;
  currency: string;
  platform_fee: number;
  stripe_fee: number;
  net_amount: number;
  description?: string;
  created_at: string;
  buyer?: SimpleUserRef;
  seller?: SimpleUserRef;
  post?: SimplePostRef;
}

export interface Sale {
  id: string;
  post: Post | SimplePostRef;
  buyer: User | SimpleUserRef;
  sale_price: number;
  commission_rate: number;
  commission_amount: number;
  artist_earnings: number;
  delivery_method: 'digital' | 'physical' | 'both';
  delivery_status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  created_at: string;
}

export interface PaymentSettings {
  stripe_account_id?: string;
  auto_payout: boolean;
  payout_schedule: 'daily' | 'weekly' | 'monthly';
  minimum_payout_amount: number;
  account_number?: string;
  tax_id?: string;
  business_name?: string;
  business_address?: Record<string, unknown>;
  tax_rate: number;
  invoice_footer?: string;
  payment_terms: string;
  holded_enabled?: boolean;
  holded_api_key?: string;
  holded_organization_id?: string;
  holded_series_id?: string;
}

interface ImportMetaEnv {
  env: Record<string, string>;
}

let cachedStripeKey: string | null = null;

export const paymentsService = {
  // Payment Methods
  async getStripePublishableKey() {
    if (cachedStripeKey) return cachedStripeKey;
    const envKey = (import.meta as unknown as ImportMetaEnv).env?.VITE_STRIPE_PUBLIC_KEY as string | undefined;
    if (!envKey) throw new Error('Missing Stripe publishable key (VITE_STRIPE_PUBLIC_KEY)');
    cachedStripeKey = envKey;
    return envKey;
  },

  async getPaymentMethods() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('payment_methods')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('is_default', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createSetupIntent() {
    const { data, error } = await supabase.functions.invoke('payments', {
      headers: {
        apikey: (import.meta as unknown as ImportMetaEnv).env.VITE_SUPABASE_ANON_KEY,
      },
      body: { action: 'create_setup_intent' },
    });

    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Unable to create setup intent');
    return data.data as {
      client_secret: string;
      customer_id: string;
      publishable_key: string;
    };
  },

  async createPaymentIntent(amount: number, currency: string = 'eur', metadata: Record<string, unknown> = {}) {
    const { data, error } = await supabase.functions.invoke('payments', {
      headers: {
        apikey: (import.meta as unknown as ImportMetaEnv).env.VITE_SUPABASE_ANON_KEY,
      },
      body: { 
        action: 'create_payment_intent',
        amount,
        currency,
        metadata
      },
    });

    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Unable to create payment intent');
    return data.data as {
      client_secret: string;
      payment_intent_id: string;
    };
  },

  async confirmPayment(orderId: string) {
    const { data, error } = await supabase.functions.invoke('payments', {
      headers: {
        apikey: (import.meta as unknown as ImportMetaEnv).env.VITE_SUPABASE_ANON_KEY,
      },
      body: { 
        action: 'confirm_payment',
        order_id: orderId
      },
    });

    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Unable to confirm payment');
    return data.data;
  },

  async addPaymentMethod(stripePaymentMethodId: string, options?: { makeDefault?: boolean; billingName?: string }) {
    const { data, error } = await supabase.functions.invoke('payments', {
      headers: {
        apikey: (import.meta as unknown as ImportMetaEnv).env.VITE_SUPABASE_ANON_KEY,
      },
      body: {
        action: 'attach_payment_method',
        payment_method_id: stripePaymentMethodId,
        make_default: options?.makeDefault ?? true,
        billing_details: options?.billingName ? { name: options.billingName } : undefined,
      },
    });

    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Unable to attach payment method');
    return data.data;
  },

  async setDefaultPaymentMethod(paymentMethodId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First, set all methods to non-default
    await supabase
      .from('payment_methods')
      .update({ is_default: false })
      .eq('user_id', user.id);

    // Then set the selected one as default
    const { data, error } = await supabase
      .from('payment_methods')
      .update({ is_default: true })
      .eq('id', paymentMethodId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async removePaymentMethod(paymentMethodId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    try {
      const { data, error } = await supabase.functions.invoke('payments', {
        headers: {
          apikey: (import.meta as unknown as ImportMetaEnv).env.VITE_SUPABASE_ANON_KEY,
        },
        body: {
          action: 'detach_payment_method',
          payment_method_id: paymentMethodId,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Unable to remove payment method');
      return;
    } catch (err) {
      console.warn('paymentsService.removePaymentMethod falling back to direct update', err);
      const { error: fallbackError } = await supabase
        .from('payment_methods')
        .update({ is_active: false, is_default: false })
        .eq('id', paymentMethodId)
        .eq('user_id', user.id);

      if (fallbackError) throw fallbackError;
    }
  },

  // Transactions
  async getTransactions(options?: {
    type?: 'sale' | 'refund' | 'payout' | 'fee' | 'all';
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('transactions')
      .select(`
        *,
        buyer:profiles!transactions_buyer_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        ),
        seller:profiles!transactions_seller_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        ),
        post:posts (
          id,
          title,
          media_files (
            url,
            type
          )
        )
      `)
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`);

    if (options?.type && options.type !== 'all') {
      query = query.eq('type', options.type);
    }

    if (options?.status) {
      query = query.eq('status', options.status);
    }

    query = query.order('created_at', { ascending: false });

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  // Sales
  async getSales(isSeller: boolean = true) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('sales')
      .select(`
        *,
        post:posts (
          id,
          title,
          description,
          media_files (
            url,
            type
          )
        ),
        buyer:profiles!sales_buyer_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        ),
        seller:profiles!sales_seller_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        ),
        transaction:transactions (
          status,
          created_at
        )
      `)
      .eq(isSeller ? 'seller_id' : 'buyer_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getOrders(isBuyer: boolean = true) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        post:posts (
          id,
          title,
          media_files (
            url,
            type
          )
        ),
        seller:profiles!orders_seller_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        )
      `)
      .eq(isBuyer ? 'buyer_id' : 'seller_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async updateDeliveryStatus(saleId: string, status: string, trackingNumber?: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const updateData: Record<string, unknown> = {
      delivery_status: status,
      updated_at: new Date().toISOString(),
    };

    if (trackingNumber) {
      updateData.delivery_tracking = trackingNumber;
    }

    if (status === 'delivered') {
      updateData.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('sales')
      .update(updateData)
      .eq('id', saleId)
      .eq('seller_id', user.id)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (!data) throw new Error('Sale record not found');
    return data;
  },

  // Payment Settings
  async getPaymentSettings() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('payment_settings')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;
    
    // Return default settings if none exist
    return data || {
      auto_payout: false,
      payout_schedule: 'weekly',
      minimum_payout_amount: 20.00,
      tax_rate: 0,
      payment_terms: 'Payment due within 30 days',
    };
  },

  async updatePaymentSettings(settings: Partial<PaymentSettings>) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('payment_settings')
      .upsert({
        user_id: user.id,
        ...settings,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async generateBuyerReceipt(
    order: { id: string; created_at: string; buyer_name?: string; buyer_email?: string; final_price: number },
    post: { title: string; author: { username?: string; full_name?: string } }
  ) {
    const generator = new PDFGenerator();
    await generator.addReceipt({
      orderId: order.id,
      date: order.created_at,
      buyerName: order.buyer_name,
      buyerEmail: order.buyer_email,
      items: [{
        title: post.title,
        price: order.final_price,
        artistName: post.author.username || post.author.full_name || 'Unknown Artist'
      }],
      total: order.final_price
    });
    return { pdfBase64: generator.getOutput() };
  },

  async generateCommissionInvoice(orderId: string) {
    const { data, error } = await supabase.functions.invoke('holded-integration', {
      headers: {
        apikey: (import.meta as unknown as ImportMetaEnv).env.VITE_SUPABASE_ANON_KEY,
      },
      body: {
        action: 'create_commission_invoice',
        order_id: orderId
      },
    });

    if (error) throw error;
    
    // PDF Download is now handled in the component (SalesView.tsx)
    // to ensure better control over the UI flow and alerts.
    
    return data;
  },

  async syncOrderWithHolded(orderId: string) {
    console.warn('syncOrderWithHolded is deprecated. Use generateCommissionInvoice instead.');
    return this.generateCommissionInvoice(orderId);
  },

  // Cart & Wishlist
  async getCartItems() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('cart_items')
      .select(`
        *,
        post:posts (
          id,
          title,
          price,
          for_sale,
          media_files (
            url,
            type
          ),
          profiles!posts_author_id_fkey (
            username,
            full_name
          )
        )
      `)
      .eq('user_id', user.id)
      .order('added_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async addToCart(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('cart_items')
      .upsert({
        user_id: user.id,
        post_id: postId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async removeFromCart(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('user_id', user.id)
      .eq('post_id', postId);

    if (error) throw error;
  },

  async getWishlistItems() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('wishlist_items')
      .select(`
        *,
        post:posts (
          id,
          title,
          price,
          for_sale,
          media_files (
            url,
            type
          ),
          profiles!posts_author_id_fkey (
            username,
            full_name
          )
        )
      `)
      .eq('user_id', user.id)
      .order('added_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async addToWishlist(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('wishlist_items')
      .upsert({
        user_id: user.id,
        post_id: postId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async removeFromWishlist(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('wishlist_items')
      .delete()
      .eq('user_id', user.id)
      .eq('post_id', postId);

    if (error) throw error;
  },

  // Analytics
  async getEarningsOverview(period: 'week' | 'month' | 'quarter' | 'year' = 'month') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const endDate = new Date();
    const startDate = new Date();

    switch (period) {
      case 'week':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
    }

    const { data, error } = await supabase
      .from('sales')
      .select(`
        artist_earnings,
        commission_amount,
        sale_price,
        created_at
      `)
      .eq('seller_id', user.id)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString());

    if (error) throw error;

    const totalEarnings = data?.reduce((sum, sale) => sum + parseFloat(sale.artist_earnings.toString()), 0) || 0;
    const totalSales = data?.reduce((sum, sale) => sum + parseFloat(sale.sale_price.toString()), 0) || 0;
    const totalCommissions = data?.reduce((sum, sale) => sum + parseFloat(sale.commission_amount.toString()), 0) || 0;
    const salesCount = data?.length || 0;

    return {
      totalEarnings,
      totalSales,
      totalCommissions,
      salesCount,
      averageSaleValue: salesCount > 0 ? totalSales / salesCount : 0,
    };
  },
};
